import React, { useEffect, useMemo, useState } from 'react'
import { LicenseCard } from '../../components/LicenseCard'
import Button from '../../components/Button'
import PurchaseDetailsModal from '../../components/PurchaseDetailsModal'
import { useGetLicenseProductsListApiMutation, useGetTriningCoursesListApiMutation } from '../../redux/api/getLicenseProductsApi'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { useLocation } from 'react-router-dom'
import { formatAmount } from '../../utilities/helpers'
import { commercialLicensesLabel, StudentLicensesLabel, TrainingCoursesLabel } from '../../utilities/pageConstants'
import { commonLabelName } from '../../utilities/commonConstants'

/**
 * Represents a license available for purchase.
 */
interface License {
    ProductCode: string
    id: string
    title: string
    term: string
    imageSrc: string
    unitPricewithoutMaintenance: string | 'Free'
    price: string | 'Free'
    type: 'priced' | 'free'
}

interface StudentLicensesProps {
    selectedLicenses: Set<string> | any
    setSelectedLicenses: React.Dispatch<React.SetStateAction<Set<string>>> | any
    quantities: Record<string, number> | any
    setQuantities: React.Dispatch<React.SetStateAction<Record<string, number>>> | any
    setMainTab: React.Dispatch<React.SetStateAction<'productDownloads' | 'myLicenses' | 'commercial' | 'academic' | 'training'>> | any
    selectedLicensesDetails:
        | {
              ProductCode: string
              ProductName: string
              Quantity: number
              Price: string
              UiPrice: string
              Term: string
          }[]
        | any
    setSelectedLicensesDetails:
        | React.Dispatch<
              React.SetStateAction<
                  {
                      ProductCode: string
                      ProductName: string
                      Quantity: number
                      Price: string
                      UiPrice: string
                      Term: string
                  }[]
              >
          >
        | any
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>> | any
    isModalOpen: boolean | any
}

/**
 * CommercialLicenses Component - Handles the selection and management of commercial licenses.
 * Users can select licenses, adjust quantities, and view license details.
 *
 * @component
 */
const CommercialLicenses: React.FC<StudentLicensesProps> = ({
    selectedLicenses,
    setSelectedLicenses,
    setMainTab,
    quantities,
    setQuantities,
    selectedLicensesDetails,
    setSelectedLicensesDetails,
    setModalOpen,
    isModalOpen
}) => {
    const location = useLocation()

    /** Stores selected license IDs */
    // const [selectedLicenses, setSelectedLicenses] = useState<Set<string>>(new Set())
    const [selectedMainTab, setSelectedMainTab] = useState<'commercialLicenses' | 'trainingCourses'>('commercialLicenses')

    /** API mutation to fetch license products list */
    const [GetLicenseProductsList, { isLoading }] = useGetLicenseProductsListApiMutation()
    const [GetTrainingCoursesList, { isLoading: trainingCoursesLoading }] = useGetTriningCoursesListApiMutation()

    // /** Stores the quantity of each selected license */
    // const [quantities, setQuantities] = useState<Record<string, number>>({})

    // /** Controls the modal visibility */
    // const [isModalOpen, setModalOpen] = useState(false)

    /** Stores the list of available licenses */
    const [licenses, setLicenses] = useState<License[]>([])

    /** Stores details of selected licenses */
    // const [selectedLicensesDetails, setSelectedLicensesDetails] = useState<
    //     { ProductCode: string; ProductName: string; Quantity: number; Price: string; Term: string }[]
    // >([])

    const commercialLicenses = ['IDLLIC', 'IDLTLA', 'ENVIDLLIC', 'ENVIDLTLA', 'ENVLIC', 'ENVTLA', 'ENVACMLIC', 'ENVACMTLA']
    const trainingCourses = ['ELNISRS', 'ELNEBG', 'ELNAEG', 'ELNEFUN']

    // Determine the type dynamically based on selected licenses
    const computedType = useMemo(() => {
        const selectedCodes = Array.from(selectedLicenses) as string[]
        const hasStudentLicense = selectedCodes.some((code) => commercialLicenses.includes(code))
        const hasTraining = selectedCodes.some((code) => trainingCourses.includes(code))

        if (hasStudentLicense && hasTraining) return 'COMMERCIAL,TRAINING'
        if (hasStudentLicense) return 'COMMERCIAL'
        if (hasTraining) return 'COMMERCIALTRAININGONLY'
        return '' // Default fallback
    }, [selectedLicenses])

    /**
     * Fetches the list of available commercial licenses or training courses based on the selected tab.
     */
    useEffect(() => {
        const fetchData = async () => {
            try {
                if (selectedMainTab === 'commercialLicenses') {
                    const response = await GetLicenseProductsList({
                        flow: 'Commercial',
                        licenseType: 'Priced'
                    }).unwrap()

                    const perpetualProductCodes = ['ENVIDLLIC', 'ENVLIC', 'IDLLIC', 'ENVACMLIC']

                    const transformedLicenses: License[] = response?.data?.map((item: any) => ({
                        ProductCode: item.ProductCode,
                        title: item.Name,
                        term: perpetualProductCodes.includes(item.ProductCode) ? 'Perpetual License' : '12 Month Term License',
                        unitPricewithoutMaintenance: item?.UnitPrice,
                        price: (selectedMainTab === 'commercialLicenses' && item.UnitPricewithMaintenance) || '',
                        imageSrc: item.imageSrc,
                        type: 'priced'
                    }))

                    setLicenses(transformedLicenses)
                } else if (selectedMainTab === 'trainingCourses') {
                    const response = await GetTrainingCoursesList({
                        flow: 'Commercial',
                        licenseType: 'training'
                    }).unwrap()

                    const transformedCourses: License[] = response?.data?.map((item: any) => ({
                        ProductCode: item.ProductCode,
                        title: item.Name,
                        term: '2 Months Duration',
                        price: item.UnitPrice,
                        imageSrc: item.imageSrc,
                        type: 'priced'
                    }))

                    setLicenses(transformedCourses)
                }
            } catch (error) {
                console.error('Error fetching data:', error)
            }
        }

        fetchData()
    }, [selectedMainTab, GetLicenseProductsList, GetTrainingCoursesList])

    /**
     * Handles the selection and deselection of a license.
     *
     * @param {string} id - The ID of the license being toggled.
     * @returns {(event: React.ChangeEvent<HTMLInputElement>) => void} Event handler for the checkbox.
     */
    const toggleLicense = (id: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const newSelected = new Set(selectedLicenses)
        // Ensure the type of licenses is correctly enforced
        const selectedLicense = licenses?.find((license: License) => license?.ProductCode === id)
        console.log(selectedLicense, 'selectedLicense')

        if (!selectedLicense) return
        const licenseDetails = {
            ProductCode: selectedLicense?.ProductCode || '',
            ProductName: selectedLicense?.title || '',
            Quantity: quantities[id] || 0,
            Price:
                selectedMainTab === 'commercialLicenses'
                    ? String(selectedLicense?.unitPricewithoutMaintenance)
                    : selectedMainTab === 'trainingCourses'
                      ? String(selectedLicense?.price)
                      : '0',
            UiPrice: selectedLicense?.price,
            Term: selectedLicense?.term
        }

        if (event.target.checked) {
            newSelected.add(id)
            setSelectedLicensesDetails((prev: any) => [...prev, licenseDetails])
        } else {
            newSelected.delete(id)
            setSelectedLicensesDetails((prev: any[]) => prev.filter((item) => item?.ProductCode !== id))
        }
        setSelectedLicenses(newSelected)
    }

    /**
     * Updates the quantity of a selected license.
     * If quantity is set to 0, the license is removed from the selected list.
     *
     * @param {string} id - The ID of the license.
     * @param {number} newQuantity - The updated quantity.
     */
    const handleQuantityChange = (id: string, newQuantity: number) => {
        setQuantities((prev: any) => {
            const updatedQuantities = {
                ...prev,
                [id]: newQuantity > 0 ? newQuantity : 0
            }

            // If the quantity is 0, remove the license from selectedLicenses and selectedLicensesDetails
            if (newQuantity === 0) {
                const newSelected = new Set(selectedLicenses)
                newSelected.delete(id)
                setSelectedLicenses(newSelected)

                // Remove from selectedLicensesDetails state
                setSelectedLicensesDetails((prevDetails: any[]) =>
                    prevDetails.filter((item: { ProductCode: string }) => item.ProductCode !== id)
                )
            }

            return updatedQuantities
        })
    }

    /**
     * Renders the list of available licenses as `LicenseCard` components.
     *
     * @param {License[]} licenses - The list of licenses to be displayed.
     * @returns {JSX.Element | null} A list of `LicenseCard` components or null if no licenses exist.
     */
    const renderLicenses = (licenses: License[]) => {
        return (
            licenses &&
            licenses?.length > 0 &&
            licenses?.map((license) => (
                <LicenseCard
                    key={license?.ProductCode}
                    productCode={license?.ProductCode}
                    title={license.title}
                    term={license.term}
                    imageSrc={license.imageSrc}
                    price={formatAmount(Number(license.price))}
                    isSelected={selectedLicenses.has(license.ProductCode)}
                    onSelect={toggleLicense(license.ProductCode)}
                    setQuantity={(newQuantity: number) => handleQuantityChange(license.ProductCode, newQuantity)}
                    quantity={quantities[license.ProductCode]}
                />
            ))
        )
    }

    return (
        <div className='p-6 max-w-7xl mx-auto border border-gray bg-[#f9f9f9] rounded-lg'>
            <div className='flex justify-between'>
                <div className='flex border-b border-[#e1e1e1] gap-16 mt-4'>
                    {/* <button
                        className={`px-4 py-2 text-lg font-semibold ${
                            selectedMainTab === 'commercialLicenses' ? 'text-primary border-b-2 border-primary' : 'text-secondary'
                        }`}
                        onClick={() => setSelectedMainTab('commercialLicenses')}
                    >
                        {StudentLicensesLabel?.commercialLicenses}
                    </button>
                    <button
                        className={`px-4 py-2 text-lg font-semibold ${
                            selectedMainTab === 'trainingCourses' ? 'text-primary border-b-2 border-primary' : 'text-secondary'
                        }`}
                        onClick={() => setSelectedMainTab('trainingCourses')}
                    >
                        {TrainingCoursesLabel?.trainingCourses}
                    </button> */}
                </div>
            </div>
            <div className='ml-2'>
                <div className='text-secondary text-center font-normal'>
                    <p>Online credit card purchase is available only in the US in USD currency.To order products that</p>
                    <p className='font-normal'>
                        are not shown here, please{' '}
                        <a
                            href='https://www.nv5geospatialsoftware.com/Company/Contact-Us'
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-primary underline font-semibold'
                        >
                            Contact your Sales Representative
                        </a>
                        &nbsp;or&nbsp;
                        <a
                            href='https://www.nv5geospatialsoftware.com/Request-a-Quote'
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-primary underline font-semibold'
                        >
                            Request a Quote
                        </a>
                        .
                    </p>
                    <p>
                        Problems? &nbsp;
                        <a
                            href='mailto:geospatialsupport@nv5.com'
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-primary underline font-semibold'
                        >
                            geospatialsupport@nv5.com
                        </a>
                    </p>
                </div>
            </div>
            <div className='mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6'>
                {isLoading || trainingCoursesLoading ? (
                    Array.from({ length: 4 }).map((_, index) => (
                        <div key={index} className='bg-gray-200 rounded-lg h-24 p-2 mb-4'>
                            <Skeleton height={100} />
                        </div>
                    ))
                ) : licenses.length > 0 ? (
                    renderLicenses(licenses)
                ) : (
                    <div className='text-center col-span-2 py-10 text-gray-500'>{commercialLicensesLabel?.NoLicensesAvailable}</div>
                )}
            </div>

            <div className='flex justify-end mt-6'>
                <Button
                    variant='primary'
                    className={`px-6 py-2 bg-primary text-white rounded-md ${
                        selectedLicenses.size === 0 ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    disabled={selectedLicenses.size === 0}
                    onClick={() => {
                        setModalOpen(true)
                    }}
                >
                    {commonLabelName?.proceed}
                </Button>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <PurchaseDetailsModal
                    licenses={selectedLicensesDetails}
                    quantities={quantities}
                    onClose={() => setModalOpen(false)}
                    type={computedType}
                    setMainTab={setMainTab}
                    setSelectedLicenses={setSelectedLicenses}
                    setQuantities={setQuantities}
                    setSelectedLicensesDetails={setSelectedLicensesDetails}
                />
            )}
        </div>
    )
}

export default CommercialLicenses
